import { useMemo } from 'react'
import { searchData, type SearchItem } from '@/config/searchData'
import { useAuth } from '@/hooks/useAuth'

interface SearchResult extends SearchItem {
  matchScore: number
  matchedText?: string
}

// Helper function to get language with fallback
function getLanguageKey(lang: string): keyof SearchItem['title'] {
  // Direct match
  if (lang === 'en' || lang === 'zh' || lang === 'zh_CN' || lang === 'jp' || lang === 'ko') {
    return lang as keyof SearchItem['title']
  }
  // Fallback for zh_CN -> zh
  if (lang.startsWith('zh')) {
    return 'zh'
  }
  // Default fallback to en
  return 'en'
}

// Helper function to get text with fallback
function getText<T extends { [key: string]: any }>(
  obj: T,
  lang: string,
  fallbackLang: keyof T
): T[keyof T] | undefined {
  const langKey = getLanguageKey(lang) as keyof T
  return obj[langKey] || obj[fallbackLang]
}

export function useSearch(query: string, currentLanguage: string) {
  const { user } = useAuth()

  const results = useMemo(() => {
    // If no query, return empty results
    if (!query || query.trim().length === 0) {
      return []
    }

    const searchTerm = query.toLowerCase().trim()
    const matchedItems: SearchResult[] = []
    const effectiveLang = getLanguageKey(currentLanguage)

    // Filter items based on permissions
    const filteredData = searchData.filter(item => {
      // Check local user requirement
      if (item.requireLocalUser && user?.type !== 'local') {
        return false
      }
      return true
    })

    // Search through filtered items
    filteredData.forEach(item => {
      let matchScore = 0
      let matchedText = ''

      // Check current language with fallback
      const title = getText(item.title, currentLanguage, 'en')
      if (title && typeof title === 'string' && title.toLowerCase().includes(searchTerm)) {
        matchScore += 100
        matchedText = title
      }

      // Check description match (medium priority)
      if (item.description) {
        const description = getText(item.description, currentLanguage, 'en')
        if (description && typeof description === 'string' && description.toLowerCase().includes(searchTerm)) {
          matchScore += 50
          if (!matchedText) matchedText = description
        }
      }

      // Check keywords match (lower priority)
      const keywords = getText(item.keywords, currentLanguage, 'en')
      if (keywords && Array.isArray(keywords)) {
        keywords.forEach(keyword => {
          if (typeof keyword === 'string' && keyword.toLowerCase().includes(searchTerm)) {
            matchScore += 30
            if (!matchedText) matchedText = keyword
          }
        })
      }

      // Also check all other languages for better results
      Object.keys(item.title).forEach(lang => {
        if (lang !== effectiveLang) {
          const otherTitle = item.title[lang as keyof typeof item.title]
          if (otherTitle && typeof otherTitle === 'string' && otherTitle.toLowerCase().includes(searchTerm)) {
            matchScore += 80
            if (!matchedText) matchedText = otherTitle
          }

          const otherKeywords = item.keywords[lang as keyof typeof item.keywords]
          if (otherKeywords && Array.isArray(otherKeywords)) {
            otherKeywords.forEach(keyword => {
              if (typeof keyword === 'string' && keyword.toLowerCase().includes(searchTerm)) {
                matchScore += 20
                if (!matchedText) matchedText = keyword
              }
            })
          }
        }
      })

      // If there's a match, add to results
      if (matchScore > 0) {
        matchedItems.push({
          ...item,
          matchScore,
          matchedText
        })
      }
    })

    // Sort by match score (descending)
    return matchedItems.sort((a, b) => b.matchScore - a.matchScore)
  }, [query, currentLanguage, user])

  return results
}
